import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { MANAGER_ADDRESS_REASON_MAX, VAULTS_NS as NS } from './constants';
import type { VaultManagerAddressForm } from './useVaultManagerAddress';

/**
 * XBIT-4258: change an official vault's manager address to another wallet of the same manager. The new address, a reason
 * (kept on both audit trails) and the vault's id typed to confirm. The note says what this does not do.
 */
export const ManagerAddressDialog = ({
  form,
  vaultId,
  vaultName,
}: {
  form: VaultManagerAddressForm;
  vaultId: string;
  vaultName: string;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) =>
    translate(`${NS}.detail.managerAddress.dialog.${key}`, options);
  const count = Array.from(form.reason.trim()).length;

  return (
    <Dialog
      open={form.isOpen}
      onClose={form.submitting ? undefined : form.close}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{t('title', { name: vaultName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">{t('body')}</Typography>
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          <TextField
            required
            fullWidth
            id="vault-manager-address"
            label={t('address')}
            value={form.address}
            error={Boolean(form.errors.address)}
            helperText={form.errors.address ? translate(form.errors.address) : t('addressHint')}
            onChange={(e) => form.setAddress(e.target.value)}
            inputProps={{ spellCheck: false, style: { fontFamily: 'monospace' } }}
          />
          <TextField
            required
            fullWidth
            multiline
            minRows={2}
            label={t('reason')}
            value={form.reason}
            error={Boolean(form.errors.reason)}
            helperText={[
              form.errors.reason &&
                translate(form.errors.reason, { max: MANAGER_ADDRESS_REASON_MAX }),
              t('reasonHint', { count, max: MANAGER_ADDRESS_REASON_MAX }),
            ]
              .filter(Boolean)
              .join(' · ')}
            onChange={(e) => form.setReason(e.target.value)}
          />
          <TextField
            required
            fullWidth
            id="vault-manager-address-confirm"
            label={t('typeToConfirm', { id: vaultId })}
            value={form.typed}
            onChange={(e) => form.setTyped(e.target.value)}
            inputProps={{ spellCheck: false, autoComplete: 'off' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.close} disabled={form.submitting}>
          {t('cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="warning"
          onClick={form.submit}
          disabled={form.submitting || !form.confirmed}
          loading={form.submitting}
        >
          {form.alert ? t('retry') : t('submit')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
